<template>
  <div class="home">
    <section class="hero text-white py-5 wavy-bg-section">
      <div class="container position-relative" style="z-index: 2;">
        <div class="row align-items-center">
          <div class="col-md-6">
            <h1 class="display-4">Welcome to <span class="fw-bold">Stream</span><span class="fst-italic">line</span></h1>
            <p class="lead">The next generation telecom service that keeps you connected everywhere you go.</p>
            <p class="mb-4">Get unlimited data, calls, and texts with our innovative plans.</p>
            <div class="d-flex gap-2">
              <router-link to="/login" class="btn btn-light btn-lg">Get Started</router-link>
              <router-link to="/plans" class="btn btn-outline-light btn-lg">Try New Plans</router-link>
            </div>
          </div>
          <div class="col-md-6 d-none d-md-block">
            <div class="hero-carousel">
              <div v-for="(image, index) in heroImages" :key="index" 
                   class="carousel-item" 
                   :class="{ active: currentImageIndex === index }">
                <img :src="image.src" 
                     :alt="image.alt" 
                     class="img-fluid hero-image" 
                     @load="onImageLoad(index)"
                     loading="lazy" />
              </div>
              <div class="carousel-progress">
                <div v-for="(image, index) in heroImages" :key="`dot-${index}`"
                     class="carousel-dot"
                     :class="{ active: currentImageIndex === index }"
                     @click="goToSlide(index)"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="features" class="py-5">
      <div class="container">
        <h2 class="text-center mb-5 section-title">Why Choose Streamline?</h2>
        <div class="row g-4">
          <div class="col-md-4">
            <div class="card h-100 feature-card">
              <div class="card-body text-center p-4">
                <div class="feature-icon bg-primary text-white rounded-circle mb-3 mx-auto">
                  <i class="bi bi-speedometer2"></i>
                </div>
                <h3 class="card-title h5">Lightning Fast Speeds</h3>
                <p class="card-text">Experience 5G connectivity with download speeds up to 1Gbps.</p>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100 feature-card">
              <div class="card-body text-center p-4">
                <div class="feature-icon bg-primary text-white rounded-circle mb-3 mx-auto">
                  <i class="bi bi-graph-up"></i>
                </div>
                <h3 class="card-title h5">Unlimited Everything</h3>
                <p class="card-text">Unlimited data, calls, and texts with no hidden fees or throttling.</p>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card h-100 feature-card">
              <div class="card-body text-center p-4">
                <div class="feature-icon bg-primary text-white rounded-circle mb-3 mx-auto">
                  <i class="bi bi-headset"></i>
                </div>
                <h3 class="card-title h5">24/7 Support</h3>
                <p class="card-text">Our dedicated support agent is available anytime you need assistance.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="plans" class="py-5 plans-section">
      <div class="container">
        <h2 class="text-center mb-5 section-title">Our Plans</h2>
        <div class="text-center mb-4">
          <router-link to="/plans" class="btn btn-outline-primary">Try our new Value Plan Builder <i class="bi bi-arrow-right"></i></router-link>
        </div>

        <!-- Loading and error states -->
        <div v-if="loading" class="text-center">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        
        <div v-if="error" class="alert alert-danger" role="alert">
          {{ error }}
        </div>
        
        <!-- Plans with custom layout -->
        <div v-if="!loading && !error" class="plans-container">
          <!-- Dynamically display plans from the database -->
          <div v-for="plan in plans" :key="plan.id" class="plan-item">
            <!-- Popular badge if plan is marked as popular -->
            <div v-if="plan.isPopular" class="plan-badge-container">
              <span class="badge bg-warning text-dark popular-badge">Most Popular</span>
            </div>
            
            <div class="card plan-card" :class="{ 'plan-popular': plan.isPopular }">
              <div class="card-header bg-primary text-white text-center py-4">
                <h3 class="m-0">{{ plan.name }}</h3>
              </div>
              <div class="card-body text-center p-4">
                <h4 class="card-price">${{ Number(plan.price).toFixed(2) }}<span class="period">/month</span></h4>
                <ul class="list-unstyled mt-3 mb-4">
                  <!-- Data amount -->
                  <li>
                    <i class="bi bi-check-circle-fill text-primary me-2"></i>
                    {{ isUnlimited(plan.data) ? 'Unlimited' : plan.data + 'GB' }} High-Speed Data
                  </li>
                  
                  <!-- Minutes -->
                  <li>
                    <i class="bi bi-check-circle-fill text-primary me-2"></i>
                    {{ isUnlimited(plan.minutes) ? 'Unlimited' : plan.minutes }} Talk
                  </li>
                  
                  <!-- SMS -->
                  <li>
                    <i class="bi bi-check-circle-fill text-primary me-2"></i>
                    {{ isUnlimited(plan.sms) ? 'Unlimited' : plan.sms }} Text
                  </li>
                  
                  <!-- Features -->
                  <li v-for="(feature, index) in plan.features" :key="index">
                    <i class="bi bi-check-circle-fill text-primary me-2"></i>{{ feature }}
                  </li>
                </ul>
                <router-link to="/login" class="btn btn-primary btn-lg w-100">Choose Plan</router-link>
              </div>
            </div>
          </div>
          
          <!-- Fallback if no plans are returned -->
          <div v-if="plans.length === 0" class="text-center w-100">
            <p>No plans are currently available. Please check back later.</p>
          </div>
        </div>
      </div>
    </section>

    <section id="contact" class="py-5 wavy-bg-section cta-section">
      <div class="container position-relative" style="z-index: 5;">
        <div class="row">
          <div class="col-lg-8 mx-auto text-center">
            <h2 class="mb-4 section-title">Ready to get started?</h2>
            <p class="lead mb-4">Join millions of satisfied customers who have made the switch to Streamline.</p>
            <router-link to="/plans" class="btn btn-primary btn-lg px-5 py-3 fw-bold cta-button">Sign Up Now</router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { publicApi } from '@/services/api'

export default {
  name: 'HomeView',
  data() {
    return {
      heroImages: [
        { 
          src: require('@/assets/Streamline-hero-1.png'), 
          alt: 'Streamline Hero 1',
          loaded: false
        },
        { 
          src: require('@/assets/Streamline-hero-2.png'), 
          alt: 'Streamline Hero 2',
          loaded: false
        },
        { 
          src: require('@/assets/Streamline-hero-3.png'), 
          alt: 'Streamline Hero 3',
          loaded: false
        },
        { 
          src: require('@/assets/Streamline-hero-4.png'), 
          alt: 'Streamline Hero 4',
          loaded: false
        }
      ],
      currentImageIndex: 0,
      carouselInterval: null,
      imagesLoaded: 0,
      plans: [],
      loading: false,
      error: null
    }
  },
  mounted() {
    this.startCarousel();
    this.fetchPlans();
  },
  beforeUnmount() {
    this.stopCarousel();
  },
  methods: {
    async fetchPlans() {
      this.loading = true;
      this.error = null;
      try {
        const response = await publicApi.get('/account/public-plans');
        console.log('Plans data received:', JSON.stringify(response.data, null, 2));
        
        // Ensure features are properly parsed
        this.plans = response.data.map(plan => {
          // If features is a string, try to parse it
          if (typeof plan.features === 'string') {
            try {
              plan.features = JSON.parse(plan.features);
            } catch (e) {
              console.error('Error parsing features for plan:', plan.name, e);
              plan.features = [];
            }
          }
          return plan;
        });
        
        console.log('Plans after processing:', JSON.stringify(this.plans, null, 2));
      } catch (err) {
        console.error('Error fetching plans:', err);
        this.error = 'Failed to load plans. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
    startCarousel() {
      // Only start carousel when at least the first image is loaded
      if (this.imagesLoaded === 0) {
        // Check again in 100ms
        setTimeout(() => this.startCarousel(), 100);
        return;
      }
      
      this.carouselInterval = setInterval(() => {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.heroImages.length;
      }, 5000);
    },
    stopCarousel() {
      if (this.carouselInterval) {
        clearInterval(this.carouselInterval);
      }
    },
    onImageLoad(index) {
      this.heroImages[index].loaded = true;
      this.imagesLoaded++;
      
      // If this is the first image loaded, ensure it's displayed
      if (this.imagesLoaded === 1) {
        this.currentImageIndex = index;
      }
    },
    goToSlide(index) {
      // Allow manual navigation with dots
      this.currentImageIndex = index;
      
      // Reset interval timer when manually changing slides
      this.stopCarousel();
      this.startCarousel();
    },
    // Helper method to format data limit for display
    formatDataLimit(limit) {
      if (limit === -1) return 'Unlimited';
      return `${limit}GB`;
    },
    // Helper method to determine if a plan is unlimited
    isUnlimited(limit) {
      return limit === -1;
    },
    // Helper method to format price for display
    formatPrice(price) {
      return `$${price.toFixed(2)}`;
    }
  }
}
</script>

<style scoped>
.hero {
  padding: 80px 0;
  position: relative;
  overflow: hidden;
}

.wavy-bg-section {
  background-image: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  position: relative;
  overflow: hidden;
}

.wavy-bg-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="rgba(0,0,0,0.2)" d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,250.7C960,267,1056,245,1152,224C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>');
  background-size: cover;
  background-position: center;
  opacity: 0.5;
  z-index: 1;
}

.hero::after {
  content: '';
  position: absolute;
  bottom: -50px;
  left: 0;
  width: 100%;
  height: 100px;
  background: var(--bg-color);
  clip-path: ellipse(50% 50% at 50% 100%);
  z-index: 2;
}

.hero-carousel {
  position: relative;
  width: 100%;
  height: 450px;
  overflow: hidden;
}

.carousel-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 1.5s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-item.active {
  opacity: 1;
}

.hero-image {
  max-height: 450px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.carousel-progress {
  position: absolute;
  bottom: 15px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 5;
}

.carousel-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dot.active {
  background-color: var(--primary-color);
  transform: scale(1.2);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .hero-carousel {
    height: 300px;
  }
  
  .hero-image {
    max-height: 300px;
  }
}

.section-title {
  position: relative;
  padding-bottom: 15px;
  margin-bottom: 30px;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background-color: var(--primary-color);
}

.feature-icon {
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  transition: all 0.3s ease;
}

.feature-card {
  border: none;
  overflow: hidden;
  transition: all 0.3s ease;
}

.feature-card:hover .feature-icon {
  transform: scale(1.1);
}

.card-price {
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--primary-color);
}

.period {
  font-size: 1rem;
  font-weight: normal;
  color: #6c757d;
}

.plans-section {
  background-color: var(--bg-secondary);
  position: relative;
  padding: 80px 0;
}

/* Custom Flex Layout for Plans */
.plans-container {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
  padding: 20px 0;
}

.plan-item {
  flex: 0 0 300px;
  position: relative;
  margin-top: 20px;
}

.plan-card {
  border: none;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  overflow: visible;
}

.plan-card:hover {
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.12);
}

.plan-popular {
  border: 2px solid var(--primary-color);
  z-index: 2;
}

.plan-badge-container {
  position: absolute;
  top: -15px;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 10;
}

.popular-badge {
  padding: 0.5rem 1.5rem;
  font-weight: bold;
  font-size: 0.9rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  display: inline-block;
  background-color: #FFC107 !important;
  color: #212529 !important;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .plans-container {
    gap: 20px;
  }
  
  .plan-item {
    flex: 0 0 280px;
  }
}

@media (max-width: 768px) {
  .plans-container {
    flex-direction: column;
    align-items: center;
  }
  
  .plan-item {
    width: 100%;
    max-width: 350px;
  }
}

.cta-section {
  color: white;
  padding: 100px 0;
}

.cta-button {
  position: relative;
  z-index: 10;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Dark mode specific adjustments */
[data-theme="dark"] .plans-section {
  background-color: var(--bg-secondary);
}

[data-theme="dark"] .card-price {
  color: var(--primary-color);
}

[data-theme="dark"] .period {
  color: #adb5bd;
}
</style>
